import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Clock, Pencil, Users, User as UserIcon, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import WorkerMultiSelect from './WorkerMultiSelect';

const API = `${window.__BACKEND_URL__}/api`;

const ackMap = (job) => Object.fromEntries((job.assignment_acknowledged_by || []).map((a) => [a.user_id, a.acknowledged_at]));

// One assignee pill: green check = tapped "Got it", grey clock = not yet.
const AssigneePill = ({ uid, name, ackedAt, jobId }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${ackedAt ? 'bg-[#00C896]/15 text-[#00C896] border-[#00C896]/40' : 'bg-slate-700/40 text-slate-300 border-slate-600'}`}
    title={ackedAt ? `Got it · ${new Date(ackedAt).toLocaleString()}` : 'Has not acknowledged yet'} data-testid={`job-assignee-${jobId}-${uid}`} data-acked={ackedAt ? 'true' : 'false'}>
    {ackedAt ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3 opacity-70" />}{name}
  </span>
);

// Inline crew editor (admins/supervisors): add/remove workers without the full edit dialog.
const CrewEditor = ({ job, users, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open) setIds(job.assigned_team_member_ids?.length ? [...job.assigned_team_member_ids] : (job.assigned_to ? [job.assigned_to] : []));
  }, [open, job]);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.put(`${API}/jobs/${job.id}/assignment`, { mode: 'crew', worker_ids: ids }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setOpen(false);
      onChanged?.(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not update the crew.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="text-slate-500 hover:text-[#00C896] transition-colors" title="Add or remove workers" data-testid={`edit-crew-${job.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 bg-[#1A2433] border-[#2D3748] text-white p-3" onClick={(e) => e.stopPropagation()} data-testid={`edit-crew-popover-${job.id}`}>
        <p className="text-xs font-semibold mb-2">Who is on this job?</p>
        {job.assigned_team_id && <p className="text-[11px] text-amber-300 mb-2">Team job — saving here switches it to named workers.</p>}
        <WorkerMultiSelect users={users} value={ids} onChange={setIds} testId={`edit-crew-${job.id}-workers`} />
        {err && <p className="text-xs text-red-300 mt-2" data-testid={`edit-crew-error-${job.id}`}>{err}</p>}
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`edit-crew-cancel-${job.id}`}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy || ids.length === 0} className="bg-[#00C896] text-[#0F1923] hover:bg-[#00b386]" data-testid={`edit-crew-save-${job.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save crew
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// "Assigned to" block shared by job cards: team label, single worker, or a crew with acknowledgement state.
export default function JobAssignees({ job, getUserName, canManage = false, users = [], onChanged, iconClass = 'text-[#1E88E5]' }) {
  const acks = ackMap(job);
  const crew = job.crew_names && Object.keys(job.crew_names).length ? job.crew_names : null;
  const isTeam = !!job.is_team_job;
  const multi = !isTeam && crew && Object.keys(crew).length > 1;
  const ackedCount = crew ? Object.keys(crew).filter((uid) => acks[uid]).length : (acks[job.assigned_to] ? 1 : 0);
  const total = crew ? Object.keys(crew).length : 1;
  return (
    <div className="flex items-start gap-2 text-slate-300 flex-wrap" data-testid={`job-assignees-${job.id}`}>
      {multi ? <Users className={`w-4 h-4 mt-0.5 ${iconClass}`} /> : <UserIcon className={`w-4 h-4 mt-0.5 ${iconClass}`} />}
      <span className="font-medium">Assigned to:</span>
      {isTeam ? (
        <span className="inline-flex items-center gap-2">
          <span>{`Team · ${job.team_parent_name ? `${job.team_parent_name} › ${job.team_name}` : job.team_name || ''}`}</span>
          {crew && <span className="text-[11px] text-slate-400" title="Members who tapped Got it" data-testid={`job-ack-count-${job.id}`}>{ackedCount}/{total} got it</span>}
        </span>
      ) : multi ? (
        <span className="flex flex-wrap gap-1 items-center">
          {Object.entries(crew).map(([uid, name]) => <AssigneePill key={uid} uid={uid} name={name} ackedAt={acks[uid]} jobId={job.id} />)}
          <span className="text-[11px] text-slate-400 ml-1" data-testid={`job-ack-count-${job.id}`}>{ackedCount}/{total} got it</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <span>{(crew && crew[job.assigned_to]) || getUserName?.(job.assigned_to)}</span>
          {acks[job.assigned_to] ? <Check className="w-3.5 h-3.5 text-[#00C896]" title={`Got it · ${new Date(acks[job.assigned_to]).toLocaleString()}`} data-testid={`job-ack-${job.id}`} />
            : <Clock className="w-3.5 h-3.5 text-slate-500" title="Has not acknowledged yet" data-testid={`job-ack-pending-${job.id}`} />}
        </span>
      )}
      {canManage && users.length > 0 && <CrewEditor job={job} users={users} onChanged={onChanged} />}
    </div>
  );
}
